var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from 'react';
import { Drawer } from '../drawer';
import { styled } from '../styled';
import clsx from '../utils/clsx';
import { sidebarClasses, sidebarComponentName, getSidebarUtilityClass } from './sidebar-classes';
import { SidebarContext } from './sidebar-context';
import { useMergedClasses } from '../styles/classes';
import { ChevronsLeftIcon, ChevronsRightIcon } from '../icon';
import { Tooltip } from '../tooltip';
import { useControlled } from '../utils';
var Root = styled(Drawer, { name: sidebarComponentName, slot: 'Root' })(function (_a) {
    var _b, _c;
    var theme = _a.theme, ownerState = _a.ownerState;
    var enterTransition = {
        easing: theme.transitions.easing.easeIn,
        duration: theme.transitions.duration.leavingScreen,
    };
    var leavingTransition = {
        easing: theme.transitions.easing.easeIn,
        duration: theme.transitions.duration.leavingScreen,
    };
    var top = theme.tokens.size_layout_app_top + theme.tokens.size_layout_app_bar_height;
    return _b = {
            top: top,
            zIndex: 20,
            marginRight: theme.layout.sidebarWidthCollapsed,
            transition: theme.transitions.create('margin', enterTransition),
            willChange: 'margin'
        },
        _b[theme.breakpoints.up('lg')] = {
            marginRight: ownerState.collapsed ? theme.layout.sidebarWidthCollapsed : theme.layout.sidebarWidth,
            transition: theme.transitions.create('margin', leavingTransition),
        },
        _b["& .".concat(sidebarClasses.paper)] = (_c = {
                top: top,
                overflowX: 'hidden',
                bottom: 0,
                height: 'auto',
                width: theme.layout.sidebarWidth,
                boxShadow: '16px 0px 16px rgba(0, 0, 0, 0.1)',
                willChange: 'margin, box-shadow',
                transition: theme.transitions.create(['width', 'box-shadow'], leavingTransition),
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
            },
            _c[theme.breakpoints.up('lg')] = {
                boxShadow: 'none',
            },
            _c["&.".concat(sidebarClasses.paperHovered)] = {
                width: theme.layout.sidebarWidth,
            },
            _c["&.".concat(sidebarClasses.paperCollapsed)] = {
                boxShadow: 'none',
                transition: theme.transitions.create(['width', 'box-shadow'], enterTransition),
                width: theme.layout.sidebarWidthCollapsed,
            },
            _c),
        _b;
});
var SidebarContent = styled('div')(function (_a) {
    var theme = _a.theme;
    return ({
        padding: theme.spacing(3, 2, 2),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
    });
});
var SidebarCollapse = styled('button')(function (_a) {
    var theme = _a.theme;
    return ({
        border: 0,
        flexShrink: 0,
        color: theme.tokens.color_fg_link,
        borderTop: "1px solid ".concat(theme.tokens.color_border_default),
        backgroundColor: theme.tokens.color_bg_interactive,
        padding: theme.spacing(2),
        display: 'flex',
        justifyContent: 'flex-end',
        cursor: 'pointer',
        fontSize: theme.typography.pxToRem(16),
        transition: theme.transitions.create(['background-color', 'box-shadow', 'border-color', 'color'], {
            duration: theme.transitions.duration.shortest,
        }),
        '&:focus-visible': __assign({ outline: 'none' }, theme.mixins.focusRing({ type: 'inset' })),
        '&:hover': {
            backgroundColor: theme.tokens.color_bg_interactive_hover,
        },
    });
});
var SidebarCollapseIcon = styled('span')(function (_a) {
    var theme = _a.theme;
    return ({
        height: theme.typography.pxToRem(36),
        width: theme.typography.pxToRem(36),
        fontSize: theme.typography.pxToRem(20),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    });
});
export var Sidebar = React.forwardRef(function (props, ref) {
    var propisCollapsed = props.collapsed, children = props.children, className = props.className, onCollapseChange = props.onCollapseChange, _a = props.defaultCollapsed, defaultCollapsed = _a === void 0 ? false : _a, _b = props.PaperProps, PaperProps = _b === void 0 ? {} : _b, _c = props["aria-label"], ariaLabel = _c === void 0 ? 'primary navigation' : _c, propClasses = props.classes, _d = props.labels, _e = _d === void 0 ? {} : _d, _f = _e.collapse, collapseLabel = _f === void 0 ? 'Collapse navigation' : _f, _g = _e.expand, expandLabel = _g === void 0 ? 'Expand navigation' : _g, drawerProps = __rest(props, ["collapsed", "children", "className", "onCollapseChange", "defaultCollapsed", "PaperProps", 'aria-label', "classes", "labels"]);
    var _h = __read(useControlled({
        controlled: propisCollapsed,
        default: defaultCollapsed,
        name: 'SidebarCollapsed',
    }), 2), isCollapsed = _h[0], setIsCollapsed = _h[1];
    var classes = useMergedClasses(sidebarClasses, getSidebarUtilityClass, propClasses);
    var handleCollapseChange = React.useCallback(function () {
        var newValue = !isCollapsed;
        setIsCollapsed(newValue);
        if (onCollapseChange) {
            onCollapseChange(newValue);
        }
    }, [isCollapsed, setIsCollapsed, onCollapseChange]);
    var contextValue = React.useMemo(function () { return ({
        expanded: !isCollapsed,
        collapsed: isCollapsed,
        onCollapseChange: handleCollapseChange,
    }); }, [isCollapsed, handleCollapseChange]);
    return (React.createElement(SidebarContext.Provider, { value: contextValue },
        React.createElement(Root, __assign({ ref: ref, ownerState: { collapsed: isCollapsed }, className: clsx(classes.root, className), anchor: "left", variant: "permanent", PaperProps: __assign(__assign(__assign({ component: 'nav' }, { 'aria-label': ariaLabel }), PaperProps), { className: clsx(classes.paper, isCollapsed && classes.paperCollapsed, PaperProps.className) }) }, drawerProps),
            React.createElement(SidebarContent, { sx: { flex: 1 } }, children),
            React.createElement(Tooltip, { enterDelay: isCollapsed ? 2000 : undefined, leaveDelay: 0, title: isCollapsed ? expandLabel : collapseLabel, placement: "right" },
                React.createElement(SidebarCollapse, { onClick: handleCollapseChange },
                    React.createElement(SidebarCollapseIcon, null, isCollapsed ? React.createElement(ChevronsRightIcon, null) : React.createElement(ChevronsLeftIcon, null)))))));
});
